import React from "react";

interface CustomButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "outline";
  isActive?: boolean;
  children: React.ReactNode;
}

/**
 * CustomButton Component
 *
 * A reusable button component with consistent styling and multiple variants.
 * Supports active states and maintains accessibility through native button attributes.
 *
 * @param variant - Style variant: "default" (filled) or "outline" (bordered)
 * @param isActive - Highlights button as active/selected (only affects default variant)
 * @param children - Content to display inside the button
 * @param className - Additional CSS classes to apply
 * @param props - All other standard HTML button attributes
 */
const CustomButton: React.FC<CustomButtonProps> = ({
  variant = "default",
  isActive = false,
  children,
  className = "",
  ...props
}) => {
  // Base styles applied to all button variants
  const baseStyles = `
    w-[192.5px] h-[46px] 4xl:w-[250px] 4xl:h-[60px]
    px-[14px]
    rounded-[8px]
    border-[0.5px]
    flex items-center justify-center gap-[10px]
    text-sm 2xl:text-base 4xl:text-lg font-medium
    transition-colors duration-200
  `;

  // Generate variant-specific styles based on props
  const getVariantStyles = (): string => {
    if (variant === "default") {
      // Default variant: filled button with active state support
      return isActive
        ? "bg-[#0BA6C7] text-white border-[#0BA6C7] hover:bg-[#089ab9] focus:ring-[#0BA6C7]"
        : "bg-[#808191] text-white border-[#808191] hover:bg-[#6e6f7f] focus:ring-[#808191]";
    } else if (variant === "outline") {
      // Outline variant: bordered button with transparent background
      return "bg-white text-[#808191] border-[#808191] hover:bg-gray-100 focus:ring-[#808191]";
    }

    // Fallback to default styles
    return "bg-[#808191] text-white border-[#808191] hover:bg-[#6e6f7f] focus:ring-[#808191]";
  };

  const variantStyles = getVariantStyles();

  return (
    <button
      className={`${baseStyles} ${variantStyles} ${className}`.trim()}
      {...props}
    >
      {children}
    </button>
  );
};

export default CustomButton;
